import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { settingsKeys } from "@/lib/query";

export interface NdPromptResponse {
  value: string;
  isDefault: boolean;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  if (!res.ok) {
    let message = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  return (await res.json()) as T;
}

export function useNdPrompt() {
  return useQuery({
    queryKey: settingsKeys.ndPrompt(),
    queryFn: async (): Promise<NdPromptResponse> => {
      const json = await api<{ data: NdPromptResponse }>("/api/settings/nd-prompt");
      return json.data;
    },
  });
}

export function useUpdateNdPrompt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (value: string) => {
      const json = await api<{ data: NdPromptResponse }>("/api/settings/nd-prompt", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      });
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: settingsKeys.all });
      toast.success("Prompt saved");
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to save prompt");
    },
  });
}

export function renderNdPrompt(template: string, vars: { solutionName?: string }): string {
  return template.replace(
    /\{\{\s*solution_name\s*\}\}/g,
    vars.solutionName?.trim() || "this solution",
  );
}
